/**
 * Kit flow (estilo Insider): seleção sequencial de cor/tamanho por item do kit.
 * Custom elements: <kit-flow-selector> (wizard) e <kit-flow-item> (1 produto).
 *
 * O bloco KitFlowCart reaproveita, quase literalmente, a lógica de atualização/abertura
 * do minicart já usada e testada em produção em sections/Compre-junto-kits-3-produtos.liquid
 * deste mesmo tema (Glozin) — inclusive a mitigação de bug conhecido com o checkout Yampi
 * (item às vezes some do minicart nativo depois de um add-to-cart múltiplo; se o minicart
 * não puder ser localizado/reaberto, cai para redirect em /cart, que é confiável com Yampi).
 */
(() => {
  'use strict';

  function getVariantOptions(variant) {
    return [variant.option1, variant.option2, variant.option3].filter(Boolean);
  }

  function findVariant(product, selectedOptions) {
    if (!product || !Array.isArray(product.variants)) return null;
    if (!selectedOptions.length) {
      return product.variants.find((v) => v.available) || product.variants[0];
    }
    return product.variants.find((variant) => {
      const variantOptions = getVariantOptions(variant);
      return selectedOptions.every((value, index) => variantOptions[index] === value);
    });
  }

  function formatMoney(cents) {
    const value = (Number(cents) / 100).toFixed(2).replace('.', ',');
    return 'R$ ' + value.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  }

  function resizeImageUrl(url, size) {
    if (!url) return url;
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, (match, ext, query) => {
      return `_${size}x.${ext}${query || ''}`;
    });
  }

  /**
   * Reabertura/atualização do minicart do tema Glozin + salvaguarda Yampi.
   * Reaproveitado de sections/Compre-junto-kits-3-produtos.liquid (já em produção).
   */
  const KitFlowCart = {
    async getFreshCart() {
      const response = await fetch(`/cart.js?kit_flow=${Date.now()}`, {
        headers: { Accept: 'application/json', 'Cache-Control': 'no-cache' },
      });
      if (!response.ok) throw new Error('Não foi possível atualizar o carrinho.');
      return response.json();
    },

    updateCartCounts(cart) {
      const count = Number((cart && cart.item_count) || 0);

      document.querySelectorAll('.cart-count-drawer').forEach((el) => {
        el.textContent = ` (${count}) `;
      });

      document.querySelectorAll('.cart-count:not(.cart-count-drawer), [data-cart-count]').forEach((el) => {
        el.textContent = count;
      });

      document.querySelectorAll('.cart-count-bubble').forEach((el) => {
        el.classList.toggle('hidden', count <= 0);
        if (el.children.length) {
          Array.from(el.children).forEach((child) => (child.textContent = count));
        } else {
          el.textContent = count;
        }
      });
    },

    dispatchCartEvents(cart) {
      const detail = cart ? { cart } : undefined;
      [document, document.documentElement, window].forEach((target) => {
        target.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail }));
        target.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail }));
        target.dispatchEvent(new CustomEvent('cart:change', { bubbles: true, detail }));
      });
    },

    async replaceMiniCartFromFreshPage() {
      const currentWrapper = document.querySelector('#minicart_wrapper');
      if (!currentWrapper) return false;

      const separator = window.location.search ? '&' : '?';
      const url = `${window.location.pathname}${window.location.search}${separator}kit_flow_refresh=${Date.now()}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: { Accept: 'text/html', 'Cache-Control': 'no-cache' },
      });
      if (!response.ok) return false;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const freshWrapper = doc.querySelector('#minicart_wrapper');
      if (!freshWrapper) return false;

      currentWrapper.innerHTML = freshWrapper.innerHTML;
      currentWrapper.className = freshWrapper.className;

      Array.from(freshWrapper.attributes).forEach((attr) => {
        if (attr.name !== 'id' && attr.name !== 'class' && attr.name !== 'style') {
          currentWrapper.setAttribute(attr.name, attr.value);
        }
      });

      currentWrapper.style.removeProperty('visibility');
      currentWrapper.style.removeProperty('opacity');
      currentWrapper.style.removeProperty('pointer-events');
      currentWrapper.style.removeProperty('display');

      return true;
    },

    bindMiniCartCloseFix() {
      if (window.__kitFlowCloseFixBound) return;
      window.__kitFlowCloseFixBound = true;

      document.addEventListener('click', (event) => {
        const closeTrigger = event.target.closest('.close-cart-button, .close-cart, .overlayminicart');
        if (!closeTrigger) return;
        setTimeout(() => KitFlowCart.closeThemeCart(), 30);
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') setTimeout(() => KitFlowCart.closeThemeCart(), 30);
      });
    },

    closeThemeCart() {
      const minicart = document.querySelector('#minicart_wrapper');
      const drawer = (minicart && minicart.querySelector('.minicart__wrapper')) || document.querySelector('.minicart__wrapper');
      const overlay = document.querySelector('.overlayminicart');

      [minicart, drawer, overlay].forEach((el) => {
        if (!el) return;
        el.classList.remove('open', 'open_drawer', 'active', 'is-open');
        el.classList.add('invisible');
        el.style.removeProperty('display');
        el.style.removeProperty('visibility');
        el.style.removeProperty('opacity');
        el.style.removeProperty('pointer-events');
        el.style.removeProperty('transform');
      });

      document.body.classList.remove('open-minicart', 'overflow-hidden', 'cart-open', 'drawer-open');
      document.documentElement.classList.remove('open-minicart', 'cart-open', 'drawer-open');
    },

    openThemeCart() {
      KitFlowCart.bindMiniCartCloseFix();

      const minicart = document.querySelector('#minicart_wrapper');
      const drawer = (minicart && minicart.querySelector('.minicart__wrapper')) || document.querySelector('.minicart__wrapper');
      const overlay = document.querySelector('.overlayminicart');

      if (!minicart && !drawer) {
        throw new Error('Minicart do tema não encontrado.');
      }

      if (minicart) {
        minicart.classList.add('open_drawer', 'active', 'is-open');
        minicart.classList.remove('invisible', 'hidden');
        minicart.style.visibility = 'visible';
        minicart.style.opacity = '1';
        minicart.style.pointerEvents = 'auto';
        minicart.style.removeProperty('display');
      }

      if (drawer) {
        drawer.classList.add('active', 'is-open');
        drawer.classList.remove('invisible', 'hidden');
        drawer.style.transform = 'translateX(0px)';
        drawer.style.opacity = '1';
        drawer.style.visibility = 'visible';
        drawer.style.pointerEvents = 'auto';
      }

      if (overlay) {
        overlay.classList.add('active', 'is-open');
        overlay.classList.remove('invisible', 'hidden');
        overlay.style.removeProperty('display');
        overlay.style.opacity = '1';
        overlay.style.visibility = 'visible';
        overlay.style.pointerEvents = 'auto';
      }
    },

    /**
     * Lança se não conseguir localizar o minicart do tema — quem chama deve
     * tratar isso como sinal pra cair no fallback de redirect pra /cart (Yampi).
     */
    async refreshAndOpen() {
      const cart = await KitFlowCart.getFreshCart();
      KitFlowCart.updateCartCounts(cart);
      KitFlowCart.dispatchCartEvents(cart);
      await KitFlowCart.replaceMiniCartFromFreshPage();
      await new Promise((resolve) => setTimeout(resolve, 120));
      KitFlowCart.openThemeCart();
      return cart;
    },
  };

  class KitFlowItem extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      const script = this.querySelector('script[data-kit-flow-product]');
      this.product = script ? JSON.parse(script.textContent) : null;
      this.quantity = Number(this.dataset.quantity || 1);
      this.colorOptionPosition = Number(this.dataset.colorOptionPosition || 0);

      this.imageEl = this.querySelector('[data-kit-flow-image]');
      this.priceEl = this.querySelector('[data-kit-flow-price]');
      this.comparePriceEl = this.querySelector('[data-kit-flow-compare-price]');
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));

      this.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', this.onOptionChange.bind(this));
      });

      this.updateAvailability();
      this.updateVariant();
    }

    getSelectedOptions() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked:not(.not-exist)');
        return checked ? checked.value : null;
      });
    }

    onOptionChange(event) {
      const isColor = Number(event.target.dataset.position) === this.colorOptionPosition;
      if (isColor) this.updateAvailability();
      this.updateVariant();
      this.dispatchEvent(new CustomEvent('kitflowitem:change', { bubbles: true }));
    }

    fieldsetForPosition(position) {
      return this.fieldsets.find((fieldset) => {
        const first = fieldset.querySelector('input[type="radio"]');
        return first && Number(first.dataset.position) === position;
      });
    }

    /** Refiltra tamanhos (e demais opções) disponíveis pra cor atualmente selecionada. */
    updateAvailability() {
      if (!this.colorOptionPosition || !this.product) return;

      const colorFieldset = this.fieldsetForPosition(this.colorOptionPosition);
      const colorInput = colorFieldset ? colorFieldset.querySelector('input:checked') : null;
      const colorValue = colorInput ? colorInput.value : null;
      if (!colorValue) return;

      this.fieldsets.forEach((fieldset) => {
        const first = fieldset.querySelector('input[type="radio"]');
        if (!first) return;
        const position = Number(first.dataset.position);
        if (position === this.colorOptionPosition) return;

        const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

        inputs.forEach((input) => {
          const available = this.product.variants.some((variant) => {
            const variantOptions = getVariantOptions(variant);
            return (
              variantOptions[this.colorOptionPosition - 1] === colorValue &&
              variantOptions[position - 1] === input.value &&
              variant.available
            );
          });

          const label = input.closest('label');
          input.classList.toggle('not-exist', !available);
          if (label) label.classList.toggle('not-exist', !available);

          if (!available && input.checked) input.checked = false;
        });

        if (!fieldset.querySelector('input:checked')) {
          const firstAvailable = inputs.find((input) => !input.classList.contains('not-exist'));
          if (firstAvailable) firstAvailable.checked = true;
        }
      });
    }

    updateVariant() {
      const selectedOptions = this.getSelectedOptions().filter(Boolean);
      this.currentVariant = findVariant(this.product, selectedOptions);
      if (!this.currentVariant) return;

      if (this.priceEl) this.priceEl.textContent = formatMoney(this.currentVariant.price * this.quantity);

      if (this.comparePriceEl) {
        if (this.currentVariant.compare_at_price > this.currentVariant.price) {
          this.comparePriceEl.textContent = formatMoney(this.currentVariant.compare_at_price * this.quantity);
          this.comparePriceEl.classList.remove('hidden');
        } else {
          this.comparePriceEl.classList.add('hidden');
        }
      }

      const image = this.currentVariant.featured_image || this.product.featured_image;
      if (image && this.imageEl) {
        this.imageEl.src = resizeImageUrl(image.src, 400);
        this.imageEl.removeAttribute('srcset');
        this.imageEl.alt = image.alt || this.product.title;
      }
    }

    optionsHaveBeenSelected() {
      return (
        this.fieldsets.every((fieldset) => !!fieldset.querySelector('input[type="radio"]:checked:not(.not-exist)')) &&
        !!this.currentVariant
      );
    }

    getSelectedVariant(kitId, index) {
      return {
        id: this.currentVariant.id,
        quantity: this.quantity,
        properties: {
          _kit_id: kitId,
          _kit_index: String(index + 1),
        },
      };
    }
  }

  class KitFlowSelector extends HTMLElement {
    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.items = Array.from(this.querySelectorAll('kit-flow-item'));
      this.currentIndex = 0;
      this.sectionId = this.dataset.section || 'kit-flow';
      this.hideErrors = this.dataset.hideErrors === 'true';

      this.prevBtn = this.querySelector('.kit-flow__button--previous');
      this.nextBtn = this.querySelector('.kit-flow__button--next');
      this.submitBtn = this.querySelector('.kit-flow__button--submit');
      this.spinner = this.submitBtn ? this.submitBtn.querySelector('.loading-overlay__spinner') : null;
      this.progressCurrentEl = this.querySelector('[data-kit-flow-progress-current]');
      this.errorWrapper = this.querySelector('.kit-flow__error-message-wrapper');
      this.errorMessage = this.errorWrapper ? this.errorWrapper.querySelector('.kit-flow__error-message') : null;

      if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext.bind(this));
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrevious.bind(this));
      if (this.submitBtn) this.submitBtn.addEventListener('click', this.onSubmit.bind(this));
      this.addEventListener('kitflowitem:change', () => this.configButtons());

      this.configButtons();
    }

    showItem(index) {
      this.items.forEach((item, i) => {
        item.hidden = i !== index;
        item.classList.toggle('is-active', i === index);
      });
      if (this.progressCurrentEl) this.progressCurrentEl.textContent = `Passo ${index + 1}`;
    }

    configButtons() {
      const currentItem = this.items[this.currentIndex];
      const canAdvance = !!currentItem && currentItem.optionsHaveBeenSelected();
      const isLast = this.currentIndex === this.items.length - 1;

      if (this.prevBtn) this.prevBtn.style.display = this.currentIndex === 0 ? 'none' : 'block';
      if (this.nextBtn) {
        this.nextBtn.style.display = isLast ? 'none' : 'block';
        this.nextBtn.disabled = !canAdvance;
      }
      if (this.submitBtn) {
        this.submitBtn.style.display = isLast ? 'block' : 'none';
        const allSelected = this.items.every((item) => item.optionsHaveBeenSelected());
        this.submitBtn.disabled = !(isLast && allSelected);
      }
    }

    onNext(event) {
      event.preventDefault();
      const currentItem = this.items[this.currentIndex];
      if (!currentItem.optionsHaveBeenSelected()) {
        this.showError('Selecione uma opção para continuar');
        return;
      }
      this.showError();
      this.currentIndex += 1;
      this.showItem(this.currentIndex);
      this.configButtons();
    }

    onPrevious(event) {
      event.preventDefault();
      this.currentIndex = Math.max(0, this.currentIndex - 1);
      this.showItem(this.currentIndex);
      this.configButtons();
    }

    showError(message) {
      if (this.hideErrors || !this.errorWrapper) return;
      this.errorWrapper.hidden = !message;
      if (message && this.errorMessage) this.errorMessage.textContent = message;
    }

    setLoading(isLoading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = isLoading;
      this.submitBtn.classList.toggle('loading', isLoading);
      if (this.spinner) this.spinner.classList.toggle('hidden', !isLoading);
    }

    async onSubmit(event) {
      event.preventDefault();
      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      const allSelected = this.items.every((item) => item.optionsHaveBeenSelected());
      if (!allSelected) {
        this.showError('Selecione as opções de todos os itens do kit');
        return;
      }

      this.showError();
      this.setLoading(true);

      const kitId = `${this.sectionId}-${Date.now()}`;
      const items = this.items.map((item, index) => item.getSelectedVariant(kitId, index));

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'Cache-Control': 'no-cache',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          let errorDescription = 'Não foi possível adicionar o kit ao carrinho.';
          try {
            const errorData = await response.json();
            errorDescription = errorData.description || errorData.message || errorDescription;
          } catch (parseError) {
            /* corpo de erro não era JSON, mantém mensagem padrão */
          }
          throw new Error(errorDescription);
        }
      } catch (error) {
        console.error('[KitFlow] Erro ao adicionar kit ao carrinho:', error);
        this.showError(error.message || 'Erro ao adicionar ao carrinho.');
        this.setLoading(false);
        return;
      }

      // A partir daqui os itens já estão no carrinho — falha nesta etapa não deve
      // impedir a compra, só cai pro /cart (mitigação conhecida de bug com Yampi).
      try {
        await KitFlowCart.refreshAndOpen();
      } catch (error) {
        console.warn('[KitFlow] Minicart indisponível, redirecionando para /cart:', error);
        window.location.href = '/cart';
        return;
      } finally {
        this.setLoading(false);
      }
    }
  }

  if (!customElements.get('kit-flow-item')) customElements.define('kit-flow-item', KitFlowItem);
  if (!customElements.get('kit-flow-selector')) customElements.define('kit-flow-selector', KitFlowSelector);
})();
